import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Package, Mail, RefreshCw, Lock, IndianRupee, TrendingUp, LogOut } from "lucide-react";
import { toast } from "sonner";
import api, { inr, formatApiErrorDetail } from "@/lib/api";
import Reveal from "@/components/Reveal";

export default function Admin() {
  const [code, setCode] = useState(() => sessionStorage.getItem("abhivesha_admin") || "");
  const [error, setError] = useState("");
  const [tab, setTab] = useState("orders");

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["admin-data", code],
    enabled: !!code,
    queryFn: async () => {
      const [orders, enquiries] = await Promise.all([
        api.get("/admin/orders", { headers: { "X-Admin-Passcode": code } }),
        api.get("/admin/enquiries", { headers: { "X-Admin-Passcode": code } }),
      ]);
      return { orders: orders.data, enquiries: enquiries.data };
    },
    retry: false,
  });

  useEffect(() => {
    if (isError) {
      setError("Wrong passcode.");
      setCode("");
      sessionStorage.removeItem("abhivesha_admin");
    }
  }, [isError]);

  if (!code || isError) {
    return (
      <div className="mx-auto flex max-w-sm flex-col items-center px-4 pb-24 pt-36" data-testid="admin-gate">
        <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-forest text-cream">
          <Lock className="h-6 w-6" />
        </div>
        <h1 className="mt-5 font-display text-3xl font-extrabold text-forest">Mission control</h1>
        <p className="mt-2 text-center text-sm text-ink/60">Owner access only. Enter the dashboard passcode.</p>
        <form
          className="mt-6 w-full space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            sessionStorage.setItem("abhivesha_admin", code);
            refetch();
          }}
        >
          <input
            data-testid="admin-passcode-input"
            type="password"
            value={code}
            onChange={(e) => { setCode(e.target.value); setError(""); }}
            placeholder="Passcode"
            className="w-full rounded-xl border border-sand bg-white px-4 py-3 text-center font-mono text-sm font-bold tracking-widest focus:border-leaf"
          />
          {error && <p data-testid="admin-error" className="text-center text-xs font-bold text-red-500">{error}</p>}
          <button data-testid="admin-unlock" className="w-full rounded-full bg-forest py-3.5 text-sm font-extrabold uppercase tracking-wide text-cream hover:bg-leaf">
            Open dashboard
          </button>
        </form>
        <Link to="/" className="mt-6 text-xs font-bold uppercase tracking-wider text-ink/40 hover:text-leaf">Back to store</Link>
      </div>
    );
  }

  const orders = data?.orders || [];
  const enquiries = data?.enquiries || [];
  const paidRevenue = orders.filter((o) => o.status === "paid").reduce((s, o) => s + o.total, 0);
  const codPending = orders.filter((o) => o.status !== "paid" && o.payment_method !== "razorpay").reduce((s, o) => s + o.total, 0);
  const paymentPending = orders.filter((o) => o.status === "pending_payment").length;

  const updateStatus = async (orderId, status) => {
    try {
      await api.patch(`/admin/orders/${orderId}`, { status }, { headers: { "X-Admin-Passcode": code } });
      toast.success(`${orderId} → ${status}. Customer emailed.`);
      refetch();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const stats = [
    { icon: Package, label: "Total orders", value: orders.length, testid: "admin-stat-orders" },
    { icon: IndianRupee, label: "Paid revenue", value: inr(paidRevenue), testid: "admin-stat-revenue" },
    { icon: TrendingUp, label: "COD amount", value: inr(codPending), sub: paymentPending ? `${paymentPending} awaiting payment` : null, testid: "admin-stat-cod" },
    { icon: Mail, label: "Enquiries", value: enquiries.length, testid: "admin-stat-enquiries" },
  ];

  return (
    <div className="mx-auto max-w-6xl px-4 pb-24 pt-28 sm:px-6" data-testid="admin-dashboard">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="font-mono text-xs font-bold uppercase tracking-[0.25em] text-leaf">Owner only</p>
          <h1 className="mt-1 font-display text-4xl font-extrabold tracking-tight text-forest sm:text-5xl">MISSION CONTROL.</h1>
        </div>
        <div className="flex gap-2">
          <button data-testid="admin-refresh" onClick={() => refetch()} className="flex items-center gap-2 rounded-full border border-sand bg-white px-5 py-3 text-xs font-extrabold uppercase tracking-wide text-forest hover:border-leaf hover:text-leaf">
            <RefreshCw className={`h-3.5 w-3.5 ${isLoading ? "animate-spin" : ""}`} /> Refresh
          </button>
          <button
            data-testid="admin-lock"
            onClick={() => { sessionStorage.removeItem("abhivesha_admin"); setCode(""); }}
            className="flex items-center gap-2 rounded-full bg-forest px-5 py-3 text-xs font-extrabold uppercase tracking-wide text-cream hover:bg-leaf"
          >
            <LogOut className="h-3.5 w-3.5" /> Lock
          </button>
        </div>
      </div>

      <div className="mt-8 grid grid-cols-2 gap-4 lg:grid-cols-4">
        {stats.map((s) => (
          <div key={s.label} data-testid={s.testid} className="rounded-3xl border border-sand bg-white p-5">
            <s.icon className="h-5 w-5 text-leaf" />
            <p className="mt-3 font-display text-2xl font-extrabold text-forest">{s.value}</p>
            <p className="text-[11px] font-bold uppercase tracking-wider text-ink/50">{s.label}</p>
            {s.sub && <p className="mt-1 text-[10px] font-bold text-amber-600">{s.sub}</p>}
          </div>
        ))}
      </div>

      <div className="mt-8 flex gap-2">
        {[["orders", `Orders (${orders.length})`], ["enquiries", `Enquiries (${enquiries.length})`]].map(([id, label]) => (
          <button
            key={id}
            data-testid={`admin-tab-${id}`}
            onClick={() => setTab(id)}
            className={`rounded-full px-5 py-2.5 text-xs font-extrabold uppercase tracking-wider transition-all ${
              tab === id ? "bg-forest text-cream" : "border border-sand bg-white text-forest hover:border-leaf hover:text-leaf"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === "orders" ? (
        <div className="mt-6 overflow-x-auto rounded-3xl border border-sand bg-white" data-testid="admin-orders-table">
          <table className="w-full min-w-160 text-left text-sm">
            <thead>
              <tr className="border-b border-sand font-mono text-[10px] uppercase tracking-[0.2em] text-ink/50">
                <th className="px-5 py-4">Order</th>
                <th className="px-5 py-4">Customer</th>
                <th className="px-5 py-4">Items</th>
                <th className="px-5 py-4">Total</th>
                <th className="px-5 py-4">Payment</th>
                <th className="px-5 py-4">Status</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => (
                <tr key={o.order_id} data-testid={`admin-order-row-${o.order_id}`} className="border-b border-sand/60 last:border-0 hover:bg-cream/50">
                  <td className="px-5 py-4">
                    <p className="font-mono text-xs font-bold text-forest">{o.order_id}</p>
                    <p className="text-[10px] text-ink/40">{new Date(o.created_at).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</p>
                  </td>
                  <td className="px-5 py-4">
                    <p className="font-bold text-forest">{o.customer.name}</p>
                    <p className="text-[11px] text-ink/50">{o.customer.phone} · {o.customer.city}</p>
                  </td>
                  <td className="px-5 py-4 text-[11px] font-semibold text-ink/70">{o.items.map((i) => `${i.name.split(" ")[0]} ×${i.qty}`).join(", ")}</td>
                  <td className="px-5 py-4 font-display font-extrabold text-forest">{inr(o.total)}</td>
                  <td className="px-5 py-4 font-mono text-[10px] font-bold uppercase tracking-wider text-ink/60">{o.payment_method}</td>
                  <td className="px-5 py-4">
                    <div className="flex flex-col gap-1.5">
                      <span className={`w-fit rounded-full px-2.5 py-1 font-mono text-[9px] font-bold uppercase tracking-wider ${
                        o.status === "paid" ? "bg-leaf/15 text-leaf" : o.status === "pending_payment" ? "bg-amber-100 text-amber-700" : o.status === "shipped" ? "bg-sky-100 text-sky-700" : o.status === "delivered" ? "bg-leaf/15 text-leaf" : o.status === "cancelled" ? "bg-red-100 text-red-600" : "bg-forest/10 text-forest"
                      }`}>{o.status.replace("_", " ")}</span>
                      <select
                        data-testid={`admin-status-select-${o.order_id}`}
                        value=""
                        onChange={(e) => { if (e.target.value) updateStatus(o.order_id, e.target.value); e.target.value = ""; }}
                        className="w-fit rounded-lg border border-sand bg-cream/60 px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-forest focus:border-leaf"
                      >
                        <option value="">Update →</option>
                        <option value="shipped">Mark shipped</option>
                        <option value="delivered">Mark delivered</option>
                        <option value="cancelled">Cancel order</option>
                        <option value="placed">Revert to placed</option>
                      </select>
                    </div>
                  </td>
                </tr>
              ))}
              {orders.length === 0 && <tr><td colSpan="6" className="px-5 py-10 text-center text-sm text-ink/50">No orders yet. They land here the moment they happen.</td></tr>}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="mt-6 space-y-3" data-testid="admin-enquiries-list">
          {enquiries.map((e, i) => (
            <div key={i} className="rounded-2xl border border-sand bg-white p-5">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <p className="font-display text-sm font-extrabold text-forest">{e.name} {e.business && <span className="font-sans text-xs font-semibold text-ink/50">· {e.business}</span>}</p>
                  <p className="text-[11px] text-ink/50">{e.email} {e.phone && `· ${e.phone}`} {e.city && `· ${e.city}`}</p>
                </div>
                <span className="rounded-full bg-forest/10 px-3 py-1 font-mono text-[9px] font-bold uppercase tracking-wider text-forest">{e.btype}</span>
              </div>
              {e.message && <p className="mt-3 rounded-xl bg-cream/70 px-4 py-2.5 text-xs leading-relaxed text-ink/70">{e.message}</p>}
            </div>
          ))}
          {enquiries.length === 0 && <div className="rounded-3xl border border-dashed border-sand bg-white p-10 text-center text-sm text-ink/50">No enquiries yet.</div>}
        </div>
      )}
    </div>
  );
}
